import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, BadgeProps } from "../components/Badge";
import { Loading } from "../components/Loading";
import { DateFilter } from "../components/messages/date-filter";
import { PaymentProvider } from "../components/PaymentProvider";
import { FilterProps, PaymentFilters } from "../components/payments/PaymentFilters";
import { TipEarningsIssueIcon } from "../components/payments/TipEarningsIssueIcon";
import { useToast } from "../context/toast-context";
import { isAccreditatedLikeStatus, TipStatusPaymentEnum, TransactionStatusEnum } from "../enums/payment";
import { TransactionReqDTO } from "../services/dto/request/transactions-request-dto";
import { TransactionService } from "../services/transaction-service";
import { PaymentsResponse, PaymentTransaction } from "../types";
import { currencyFormatter, dateFormatter, formatOptionalAmount } from "../utils/format";
import { getBranchDisplayName } from "../utils/branch-display-name";

const ITEMS_PER_PAGE = 25;
const MAX_ATTEMPTS = 6;

function paymentHasTaxColumns(transactions: PaymentTransaction[]): boolean {
  return transactions.some(
    (t) => t.taxRetentionAmount != null || t.transactionNetAmount != null
  );
}


export function Payments() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [payments, setPayments] = useState<PaymentsResponse>({
    payments: [],
    totalItems: 0,
    currentPage: 0,
    totalPages: 0,
    filters: {
      branches: [],
      employees: [],
      tipStatus: [],
      transactionsStatus: [],
      providers: []
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterProps>({
    branch: [],
    employee: [],
    provider: [],
    paymentStatus: [],
    transactionStatus: [],
    minAmount: "",
    maxAmount: "",
  });

  const { showToast } = useToast();
  const { t } = useTranslation();
  const transactionService = useMemo(() => new TransactionService(), []);

  const toggleRow = (paymentId: number) => {
    setExpandedRows(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const getTransactionBadge = (status: TransactionStatusEnum, attempts: number) => {
    const statusMap: Record<TransactionStatusEnum, { variant: BadgeProps["variant"]; text: string }> = {
      [TransactionStatusEnum.UNPROCESSED]: {
        variant: "secondary",
        text: t("payments.transactions.unprocessed"),
      },
      [TransactionStatusEnum.REGISTERED]: {
        variant: "secondary",
        text: t("payments.transactions.registered"),
      },
      [TransactionStatusEnum.PENDING]: {
        variant: "pending",
        text: t("payments.transactions.pending"),
      },
      [TransactionStatusEnum.REJECTED]: {
        variant: "outline",
        text: t("payments.transactions.rejected"),
      },
      [TransactionStatusEnum.ACCREDITATED]: {
        variant: "green",
        text: t("payments.transactions.accreditated"),
      },
      [TransactionStatusEnum.FAILED]: { variant: "red", text: t("payments.transactions.failed") + ` (${t("payments.transactions.attempt")} ${attempts} ${t("pagination.of")} ${MAX_ATTEMPTS})` },
      [TransactionStatusEnum.MAX_ATTEMPTS_REACHED]: {
        variant: "red",
        text: t("payments.transactions.max_attempts_reached"),
      },
      [TransactionStatusEnum.PENDING_EPAGOS]: {
        variant: "pending",
        text: t("payments.transactions.pending"),
      },
      [TransactionStatusEnum.CLOVER_PENDING]: {
        variant: "pending",
        text: t("payments.transactions.pending"),
      },
      [TransactionStatusEnum.CLOVER_PROCESSING]: {
        variant: "pending",
        text: t("payments.transactions.pending"),
      },
      [TransactionStatusEnum.CLOVER_RETAINED]: {
        variant: "green",
        text: t("payments.transactions.accreditated"),
      },
    };

    return statusMap[status] || { variant: "secondary", text: t("payments.transactions.unknown") };
  };

  const getPaymentStatusFromTransactions = (status: TipStatusPaymentEnum) => {
    const statusMap: Record<TipStatusPaymentEnum, { variant: BadgeProps["variant"]; text: string }> = {
      [TipStatusPaymentEnum.PENDING]: {
        variant: "secondary",
        text: t("payments.transactions.pending"),
      },
      [TipStatusPaymentEnum.ACCREDITATED]: {
        variant: "green",
        text: t("payments.tips.accreditated"),
      },
      [TipStatusPaymentEnum.ERROR]: {
        variant: "red",
        text: t("payments.tips.error"),
      },
    };

    return statusMap[status] || { variant: "secondary", text: t("payments.transactions.unknown") };
  };

  const isCollectorEmployee = (employee: string): boolean => {
    return employee.toLowerCase().includes("martin dutriez");
  }

  const getTransactionDate = (transaction: PaymentTransaction) => {
    if (transaction.status === TransactionStatusEnum.REJECTED || transaction.status === TransactionStatusEnum.FAILED || transaction.status === TransactionStatusEnum.MAX_ATTEMPTS_REACHED) {
      if (transaction.rejectedDate) {
        return dateFormatter(transaction.rejectedDate, { time: true });
      }
      return "";
    }

    if (isAccreditatedLikeStatus(transaction.status)) {
      if (transaction.accreditatedDate) {
        return dateFormatter(transaction.accreditatedDate, { time: true });
      }
      return "";
    }

    return "-";
  }

  const handleApplyFilters = (newFilters: FilterProps) => {
    setFilters(newFilters);
    setPage(0);
  };

  const cleanDateFilters = () => {
    setStartDate("");
    setEndDate("");
  }

  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      const params: TransactionReqDTO = {
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
        ...(startDate && { fechadesde: startDate }),
        ...(endDate && { fechahasta: endDate }),
        ...(filters.branch.length > 0 && { branchIds: filters.branch.join(",") }),
        ...(filters.employee.length > 0 && { employeeIds: filters.employee.join(",") }),
        ...(filters.provider.length > 0 && { providerIds: filters.provider.join(",") }),
        ...(filters.paymentStatus.length > 0 && { paymentStatus: filters.paymentStatus.join(",") }),
        ...(filters.transactionStatus.length > 0 && { transactionStatus: filters.transactionStatus.join(",") }),
      };

      await transactionService.getTransactions(
        params,
        (data) => {
          setPayments(data);
        },
        (error) => {
          showToast(error, "error");
        }
      );
      setIsLoading(false);
    };

    fetchPayments();
  }, [startDate, endDate, page, filters, showToast, transactionService]);

  return (
    <main className="h-full flex flex-col rounded-3xl bg-white p-6 overflow-hidden">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <DateFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>

        <PaymentFilters 
          onApplyFilters={handleApplyFilters} 
          filterValues={payments.filters} 
          resetDateFilters={cleanDateFilters}
          totalTransferred={payments.totalTransferred}
        />
      </div>

      <div className="flex flex-col flex-1 gap-2 h-full overflow-y-auto pr-4 custom-scrollbar rounded-lg mt-4">
        <div className="flex flex-col gap-2">
          {isLoading && <Loading />}
          {!isLoading && payments.payments.length === 0 && (
            <div className="w-full h-40 flex justify-center items-center">
              <p className="text-gray-500 text-xl font-medium">
                {t("payments.no-payments")}
              </p>
            </div>
          )}
          {!isLoading && payments.payments.length > 0 && (
            <>
              <div className="overflow-x-auto -mx-1 sm:mx-0 rounded-lg">
                <div className="min-w-[36rem] sm:min-w-0">
              <div className="grid sticky top-0 bg-white z-20 grid-cols-[1rem_1fr_1fr_1fr_1fr_1fr] gap-4 py-3 font-medium text-[#484848] uppercase border-b border-[#ececec]">
                <div className="w-8"></div>
                <div>{t("payments.tips.date")}</div>
                <div>{t("payments.tips.tip-amount")}</div>
                <div>{t("payments.tips.branch")}</div>
                <div>{t("payments.tips.source")}</div>
                <div>{t("payments.tips.status")}</div>
              </div>

              <div className="flex flex-col">
                {payments.payments.map((payment) => {
                  const showTaxColumns = paymentHasTaxColumns(payment.transactions);
                  return (
                  <div key={payment.id} className="flex flex-col">
                    <div
                      className={`grid grid-cols-[1rem_1fr_1fr_1fr_1fr_1fr] items-center gap-4 py-3 cursor-pointer border-b border-[#ececec] ${
                        expandedRows.includes(payment.id)
                          ? "bg-gray-50"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => toggleRow(payment.id)}
                    >
                      <div className="flex items-start w-8">
                        {expandedRows.includes(payment.id) ? (
                          <svg
                            className="w-5 h-5 text-gray-600"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5 text-gray-600"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="text-xs tracking-wide">
                        {dateFormatter(payment.date, { time: true })}
                      </div>
                      <div className="text-sm font-semibold">
                        {currencyFormatter(payment.tipAmount)}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">
                          {getBranchDisplayName({
                            fantasyName: payment.fantasyName,
                            name: payment.branch,
                          })}
                        </span>
                        <TipEarningsIssueIcon issue={payment.earningsIssue} />
                      </div>
                      <div className="flex items-center gap-2">
                        <PaymentProvider provider={payment.origin ? payment.origin.toLowerCase() : "divix"} />
                      </div>
                      <div>
                        <Badge
                          variant={getPaymentStatusFromTransactions(payment.status).variant}
                        >
                          {getPaymentStatusFromTransactions(payment.status).text}
                        </Badge>
                      </div>
                    </div>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out transform ${
                        expandedRows.includes(payment.id)
                          ? "max-h-[5000px] opacity-100 scale-100"
                          : "max-h-0 opacity-0 scale-95"
                      }`}
                    >
                      <div className="border border-gray-200 rounded-lg mt-2 mb-4 overflow-y-auto max-h-[1000px]">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-700">
                            {t("payments.transactions.title")}
                          </h3>
                        </div>
                        {payment.transactions.length > 0 ? (
                          <div
                            className={`hidden sm:grid gap-4 py-2 px-4 text-xs font-medium text-gray-500 tracking-wide border-b border-gray-200 ${
                              showTaxColumns ? "sm:grid-cols-7" : "sm:grid-cols-5"
                            }`}
                          >
                            <div>{t("payments.transactions.date")}</div>
                            <div className="min-w-0">
                              {t("payments.transactions.tip-assigned-amount")}
                            </div>
                            {showTaxColumns && (
                              <>
                                <div className="min-w-0">
                                  {t("payments.transactions.tax-retention")}
                                </div>
                                <div className="min-w-0">
                                  {t("payments.transactions.net-to-settle")}
                                </div>
                              </>
                            )}
                            <div>{t("payments.transactions.employee")}</div>
                            <div>{t("payments.transactions.status")}</div>
                            <div className="min-w-0">
                              {t("payments.transactions.observation")}
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-4 py-2 px-4 text-xs font-medium text-gray-500 tracking-wide border-b border-gray-200">
                            <div>
                              {t("payments.transactions.pending-transactions")}
                            </div>
                          </div>
                        )}
                        {payment.transactions.map((transaction) => {
                          const txBadge = getTransactionBadge(
                            transaction.status,
                            transaction.attempts
                          );
                          return (
                          <div
                            key={transaction.id}
                            className="border-b border-gray-100 last:border-b-0 text-xs text-gray-600"
                          >
                            <div
                              className={`hidden sm:grid gap-4 py-2 px-4 items-start ${
                                showTaxColumns ? "sm:grid-cols-7" : "sm:grid-cols-5"
                              }`}
                            >
                              <div className="min-w-0">
                                {getTransactionDate(transaction)}
                              </div>
                              <div className="min-w-0 font-medium text-gray-800">
                                {currencyFormatter(transaction.amount)}
                              </div>
                              {showTaxColumns && (
                                <>
                                  <div className="min-w-0 tabular-nums">
                                    {formatOptionalAmount(
                                      transaction.taxRetentionAmount,
                                      currencyFormatter
                                    )}
                                  </div>
                                  <div className="min-w-0 tabular-nums font-medium text-gray-800">
                                    {formatOptionalAmount(
                                      transaction.transactionNetAmount,
                                      currencyFormatter
                                    )}
                                  </div>
                                </>
                              )}
                              <div className="min-w-0 break-words">
                                {isCollectorEmployee(transaction.employee)
                                  ? t("payments.transactions.collector")
                                  : transaction.employee}
                              </div>
                              <div className="min-w-0">
                                <Badge variant={txBadge.variant}>
                                  {txBadge.text}
                                </Badge>
                              </div>
                              <div className="min-w-0 break-words">
                                {isAccreditatedLikeStatus(transaction.status)
                                  ? "-"
                                  : transaction.observation || "-"}
                              </div>
                            </div>

                            <div className="sm:hidden py-3 px-4 space-y-2.5">
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500 shrink-0">
                                  {t("payments.transactions.date")}
                                </span>
                                <span className="text-right min-w-0">
                                  {getTransactionDate(transaction)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500 shrink-0">
                                  {t("payments.transactions.tip-assigned-amount")}
                                </span>
                                <span className="text-right font-medium text-gray-800 tabular-nums">
                                  {currencyFormatter(transaction.amount)}
                                </span>
                              </div>
                              {showTaxColumns && (
                                <>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500 shrink-0">
                                      {t("payments.transactions.tax-retention")}
                                    </span>
                                    <span className="text-right tabular-nums min-w-0">
                                      {formatOptionalAmount(
                                        transaction.taxRetentionAmount,
                                        currencyFormatter
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500 shrink-0">
                                      {t("payments.transactions.net-to-settle")}
                                    </span>
                                    <span className="text-right font-medium text-gray-800 tabular-nums min-w-0">
                                      {formatOptionalAmount(
                                        transaction.transactionNetAmount,
                                        currencyFormatter
                                      )}
                                    </span>
                                  </div>
                                </>
                              )}
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500 shrink-0">
                                  {t("payments.transactions.employee")}
                                </span>
                                <span className="text-right min-w-0 break-words">
                                  {isCollectorEmployee(transaction.employee)
                                    ? t("payments.transactions.collector")
                                    : transaction.employee}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3 items-center">
                                <span className="text-gray-500 shrink-0">
                                  {t("payments.transactions.status")}
                                </span>
                                <Badge variant={txBadge.variant}>
                                  {txBadge.text}
                                </Badge>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500 shrink-0">
                                  {t("payments.transactions.observation")}
                                </span>
                                <span className="text-right min-w-0 break-words">
                                  {isAccreditatedLikeStatus(transaction.status)
                                    ? "-"
                                    : transaction.observation || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
                </div>
              </div>

              <div className="flex sticky bottom-0 bg-white justify-end text-sm items-center gap-4 mt-2 pt-2">
                <button
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
                >
                  {t("pagination.previous")}
                </button>
                <span>
                  {t("pagination.page")} {page + 1} {t("pagination.of")}{" "}
                  {payments.totalPages}
                </span>
                <button
                  onClick={() =>
                    setPage((prev) =>
                      Math.min(payments.totalPages - 1, prev + 1)
                    )
                  }
                  disabled={page === payments.totalPages - 1}
                  className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
                >
                  {t("pagination.next")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
